// src/components/public_utilities/ReportFailureModal.js
// --- UPDATE (v2.0 AI triage + Chakra UI)
//   • FIX: the page renders <ReportFailureModal asset={…} onSuccess={…} /> but this modal
//     only read `onSubmit` / `assetId` / `assetName`, so every report failed with
//     "Failed to submit report" and the asset name was blank. Both prop styles now work.
//   • FIX: the form was initialised once — opening it for a second asset kept the first
//     asset's name. It now resets every time it opens.
//   • Rebuilt with Chakra UI (was Bootstrap class names that are not styled in this app).
//   • AI triage: as the reporter types, FlowSense suggests severity, category and urgency
//     from the description, the asset type, its criticality, health index and any open AI
//     alerts — with the reasons — and lists what to check on site for that asset type.
//     "Apply suggestion" fills the fields; the reporter always decides.
//   • Asset snapshot: health, data readiness and open AI insights for the asset.
//   • The submitted payload includes `ai_triage` so the backend / ML can learn from it.
// --- END UPDATE
// --- UPDATE (Public Utilities v3)
//   • Header shows the asset code (DW-BAM-0001) and constituency; `managerName` shows who is
//     responsible (MOW+ team, contractor, BPL…) so the reporter knows who will act on it.
//   • `defaultReporter` pre-fills "Reported by" with the signed-in user (still editable).
//   • The payload carries asset_code, constituency and managed_by_org_id for routing.
// --- END UPDATE
import React, { useState, useCallback, memo, useEffect, useMemo } from 'react'; // --- UPDATE: + useEffect/useMemo
import PropTypes from 'prop-types';
// --- UPDATE
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Button, FormControl, FormLabel, FormErrorMessage, FormHelperText, Input, Select, Textarea,
  Checkbox, VStack, HStack, Box, Text, Badge, Icon, Alert, AlertIcon, SimpleGrid, Flex,
  useColorModeValue, Tooltip, List, ListItem, ListIcon,
} from '@chakra-ui/react';
import {
  MdReportProblem, MdOutlineAutoAwesome, MdCheckCircleOutline, MdHealthAndSafety, MdWarning, MdPsychology,
} from 'react-icons/md';
import { triageFailure, getAssetAnalysis } from './assetAiGuide';
// --- END UPDATE

// Debug utility
class ReportFailureDebugger {
  constructor() {
    this.logs = [];
    this.renderCount = 0;
  }

  log(level, action, data = {}) {
    const logEntry = { timestamp: new Date().toISOString(), level, action, component: 'ReportFailureModal', ...data };
    this.logs.push(logEntry);
    if (this.logs.length > 500) this.logs.shift();
    // --- UPDATE: console output only in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`%c[${level}] ${action}`, `color: ${level === 'ERROR' ? '#F44336' : '#2196F3'}; font-weight: bold;`, data);
    }
    // --- END UPDATE
  }

  incrementRender() { this.renderCount++; }
  getStats() { return { totalLogs: this.logs.length, renderCount: this.renderCount }; }
}

const modalDebugger = new ReportFailureDebugger();
if (typeof window !== 'undefined') {
window.__ReportFailureDebug__ = { getLogs: () => modalDebugger.logs, getStats: () => modalDebugger.getStats() };
}

// --- UPDATE
const MAX_DESCRIPTION = 500;
const SEVERITY_SCHEME = { low: 'green', medium: 'yellow', high: 'orange', critical: 'red' };
const blankForm = (assetId, assetName, asset, defaultReporter = '') => ({ // --- UPDATE (v3): + defaultReporter
  assetId: assetId ?? asset?.id ?? '',
  assetName: assetName ?? asset?.name ?? asset?.type ?? '',
  severity: 'medium',
  category: 'mechanical',
  description: '',
  location: asset?.location_description || '',
  reportedBy: defaultReporter || '',
  images: [],
  urgentRepair: false,
});
// --- END UPDATE

const ReportFailureModal = ({ isOpen, onClose, onSubmit, assetId, assetName,
  // --- UPDATE: page-style props + AI context (all optional)
  asset, onSuccess, insights = [], sensors, alerts,
  // --- END UPDATE
  // --- UPDATE (v3)
  managerName = null, defaultReporter = '',
  // --- END UPDATE
}) => {
  modalDebugger.incrementRender();

  const [formData, setFormData] = useState(() => blankForm(assetId, assetName, asset, defaultReporter)); // --- UPDATE (v3)

  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // --- UPDATE: reset each time the modal opens (or the asset changes)
  const resolvedId = assetId ?? asset?.id;
  useEffect(() => {
    if (isOpen) {
      setFormData(blankForm(assetId, assetName, asset, defaultReporter)); // --- UPDATE (v3)
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, resolvedId]);

  const analysis = useMemo(() => {
    if (!asset) return null;
    return sensors || alerts ? getAssetAnalysis(asset, { sensors, alerts, insights }) : getAssetAnalysis(asset);
  }, [asset, sensors, alerts, insights]);
  const triage = useMemo(
    () => triageFailure({ description: formData.description, asset: asset || { type: '' }, analysis, insights }),
    [formData.description, asset, analysis, insights]
  );
  const triageDiffers = triage.severity !== formData.severity || triage.category !== formData.category || triage.urgent !== formData.urgentRepair;

  const headerBg = useColorModeValue('red.50', 'rgba(229,62,62,0.12)');
  const aiBg = useColorModeValue('purple.50', 'rgba(128,90,213,0.14)');
  const aiBorder = useColorModeValue('purple.100', 'whiteAlpha.200');
  const muted = useColorModeValue('gray.500', 'gray.400');
  const snapshotBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  // --- END UPDATE

  const validate = useCallback(() => {
    const newErrors = {};
    if (!formData.description || formData.description.length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    }
    if (!formData.reportedBy) {
      newErrors.reportedBy = 'Reporter name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault?.();
    modalDebugger.log('INFO', 'form_submit_attempted', { assetId: formData.assetId });

    if (!validate()) {
      modalDebugger.log('WARNING', 'validation_failed', { errors });
      return;
    }
    // --- UPDATE: clear message instead of a TypeError when no handler is wired
    if (typeof onSubmit !== 'function') {
      setErrors({ submit: 'Reporting is not connected on this page (missing onSubmit).' });
      return;
    }
    // --- END UPDATE

    setSubmitting(true);

    try {
      // --- UPDATE: include the AI triage so it can be audited / learned from
      const payload = {
        ...formData,
        assetType: asset?.type,
        // --- UPDATE (v3): routing context
        asset_code: asset?.asset_code || null, constituency: asset?.constituency || null, managed_by_org_id: asset?.managed_by_org_id ?? null,
        // --- END UPDATE
        ai_triage: { severity: triage.severity, category: triage.category, urgent: triage.urgent, confidence: triage.confidence, reasons: triage.reasons, accepted: !triageDiffers },
      };
      await onSubmit(payload);
      modalDebugger.log('SUCCESS', 'failure_reported', { assetId: formData.assetId });
      onSuccess?.(payload);
      // --- END UPDATE
      onClose();
    } catch (error) {
      modalDebugger.log('ERROR', 'submit_failed', { error: error.message });
      setErrors({ submit: error?.message ? `Failed to submit report: ${error.message}` : 'Failed to submit report. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  }, [formData, validate, onSubmit, onClose, errors, onSuccess, asset, triage, triageDiffers]);

  const handleChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  }, [errors]);

  // --- UPDATE
  const applyTriage = useCallback(() => {
    setFormData(prev => ({ ...prev, severity: triage.severity, category: triage.category, urgentRepair: triage.urgent }));
  }, [triage]);
  // --- END UPDATE

  if (!isOpen) return null;

  return (
    // --- UPDATE: Chakra modal (markup rebuilt; fields, validation and submit flow unchanged)
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside" isCentered>
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" overflow="hidden">
        <ModalHeader bg={headerBg}>
          <HStack spacing={3}>
            <Icon as={MdReportProblem} color="red.400" boxSize={6} />
            <Box>
              <Text fontWeight="800">Report Asset Failure</Text>
              <Text fontSize="sm" fontWeight="normal" color={muted}>
                {asset?.asset_code ? `${asset.asset_code} · ` : ''}{formData.assetName || 'Unknown asset'}{asset?.constituency ? ` · ${asset.constituency}` : ''}{/* --- UPDATE (v3) */}
              </Text>
            </Box>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody as="form" id="report-failure-form" onSubmit={handleSubmit} py={5}>
          <VStack align="stretch" spacing={4}>
            {/* Asset AI snapshot */}
            {analysis && (
              <Flex p={3} gap={3} align="center" borderRadius="lg" bg={snapshotBg} wrap="wrap" aria-label="Asset AI snapshot">
                <HStack spacing={2}>
                  <Icon as={MdHealthAndSafety} color={`${analysis.healthLevel.color}.400`} boxSize={5} />
                  <Text fontSize="xs" fontWeight="700">Health</Text>
                  <Badge colorScheme={analysis.healthLevel.color}>{analysis.health} · {analysis.healthLevel.label}</Badge>
                </HStack>
                <Text fontSize="xs" color={muted}>{analysis.familyLabel}</Text>
                {asset?.asset_criticality && <Badge colorScheme={SEVERITY_SCHEME[String(asset.asset_criticality).toLowerCase()] || 'gray'}>{asset.asset_criticality} criticality</Badge>}
                {insights.length > 0 && (
                  <Tooltip hasArrow label={insights.slice(0, 3).map(i => i.title).join(' · ')}>
                    <Badge colorScheme="purple" cursor="help">{insights.length} open AI insight{insights.length === 1 ? '' : 's'}</Badge>
                  </Tooltip>
                )}
              </Flex>
            )}

            <FormControl isRequired>
              <FormLabel fontSize="sm">Asset</FormLabel>
              <Input value={formData.assetName} isReadOnly />
            </FormControl>

            <FormControl isRequired isInvalid={!!errors.description}>
              <FormLabel fontSize="sm">Description</FormLabel>
              <Textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value.slice(0, MAX_DESCRIPTION))}
                rows={4}
                placeholder="What is wrong? e.g. 'Pump making grinding noise, pressure dropped to 20 psi since this morning'"
              />
              <FormErrorMessage>{errors.description}</FormErrorMessage>
              <FormHelperText fontSize="xs">{formData.description.length}/{MAX_DESCRIPTION} characters · symptoms help the AI triage</FormHelperText>
            </FormControl>

            {/* AI triage */}
            <Box p={3} borderRadius="lg" bg={aiBg} borderWidth="1px" borderColor={aiBorder} role="region" aria-label="AI triage">
              <Flex justify="space-between" align="center" gap={2} wrap="wrap">
                <HStack spacing={2}>
                  <Icon as={MdPsychology} color="purple.400" />
                  <Text fontSize="sm" fontWeight="800">AI triage suggestion</Text>
                  <Tooltip hasArrow label="Rule-based suggestion from the description, asset type, criticality, health and open AI alerts. You decide.">
                    <Badge variant="outline" colorScheme="purple" fontSize="9px" cursor="help">{triage.confidence} confidence</Badge>
                  </Tooltip>
                </HStack>
                <Button size="xs" colorScheme="purple" leftIcon={<Icon as={MdOutlineAutoAwesome} />} onClick={applyTriage} isDisabled={!triageDiffers}>
                  {triageDiffers ? 'Apply suggestion' : 'Applied'}
                </Button>
              </Flex>
              <HStack spacing={2} mt={2} wrap="wrap">
                <Badge colorScheme={SEVERITY_SCHEME[triage.severity]}>Severity: {triage.severity}</Badge>
                <Badge colorScheme="blue">Category: {triage.category}</Badge>
                {triage.urgent && <Badge colorScheme="red" variant="solid">Urgent repair</Badge>}
              </HStack>
              <VStack align="stretch" spacing={0.5} mt={2}>
                {triage.reasons.map((r, i) => <Text key={i} fontSize="xs" color={muted}>• {r}</Text>)}
              </VStack>
              <Text fontSize="xs" fontWeight="700" mt={2}>Check on site ({triage.familyLabel}):</Text>
              <List spacing={0.5} mt={1}>
                {triage.checks.map((c, i) => (
                  <ListItem key={i} fontSize="xs"><ListIcon as={MdCheckCircleOutline} color="purple.400" />{c}</ListItem>
                ))}
              </List>
            </Box>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Severity</FormLabel>
                <Select value={formData.severity} onChange={(e) => handleChange('severity', e.target.value)} aria-label="Severity">
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Category</FormLabel>
                <Select value={formData.category} onChange={(e) => handleChange('category', e.target.value)} aria-label="Category">
                  <option value="mechanical">Mechanical</option>
                  <option value="electrical">Electrical</option>
                  <option value="structural">Structural</option>
                  <option value="software">Software</option>
                  <option value="other">Other</option>
                </Select>
              </FormControl>
            </SimpleGrid>

            <FormControl>
              <FormLabel fontSize="sm">Location</FormLabel>
              <Input value={formData.location} onChange={(e) => handleChange('location', e.target.value)} placeholder="Street, landmark, side of road…" />
            </FormControl>

            <FormControl isRequired isInvalid={!!errors.reportedBy}>
              <FormLabel fontSize="sm">Reported By</FormLabel>
              <Input value={formData.reportedBy} onChange={(e) => handleChange('reportedBy', e.target.value)} placeholder="Your name" />
              <FormErrorMessage>{errors.reportedBy}</FormErrorMessage>
            </FormControl>

            <Checkbox isChecked={formData.urgentRepair} onChange={(e) => handleChange('urgentRepair', e.target.checked)} colorScheme="red">
              Requires urgent repair
            </Checkbox>

            {formData.severity === 'critical' && (
              <Alert status="warning" borderRadius="lg" fontSize="sm">
                <AlertIcon as={MdWarning} />
                Critical reports mark the asset as failed and alert the responsible team. Make the area safe first.
              </Alert>
            )}

            {/* --- UPDATE (v3): who acts on it */}
            <Text fontSize="xs" color={muted} data-testid="failure-routing">
              {managerName ? <>Managed by <b>{managerName}</b> — they see this report in Public Utilities and on the asset's history.</> : 'No organisation is assigned to this asset yet — assign one on the Managers tab so reports reach the right crew.'}
            </Text>
            {/* --- END UPDATE */}
            {errors.submit && (
              <Alert status="error" borderRadius="lg" fontSize="sm"><AlertIcon />{errors.submit}</Alert>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose} isDisabled={submitting}>Cancel</Button>
          <Button type="submit" form="report-failure-form" colorScheme="red" isLoading={submitting} loadingText="Submitting...">
            Submit Report
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
    // --- END UPDATE
  );
};

ReportFailureModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // --- UPDATE: optional — the page may pass `asset` + `onSuccess` instead
  onSubmit: PropTypes.func,
  assetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  assetName: PropTypes.string,
  asset: PropTypes.object,
  onSuccess: PropTypes.func,
  insights: PropTypes.array,
  sensors: PropTypes.array,
  alerts: PropTypes.array,
  // --- END UPDATE
  managerName: PropTypes.string, // --- UPDATE (v3)
  defaultReporter: PropTypes.string, // --- UPDATE (v3)
};

export default memo(ReportFailureModal);